import { c as _c } from "react/compiler-runtime";
import { GithubSsoReauthorizationError } from "@/components/GithubSsoReauthorizationError";
import { invalidateConnectedGithubOrgs, useConnectedGithubOrgsNonSuspense, type SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useQueryClient } from "react-query";

/**
 * Match the org segment of the current URL against the orgs GitHub hid from
 * the user due to a SAML SSO authorization gap. GitHub logins are
 * case-insensitive, so the comparison is too.
 */
export function findSsoBlockedOrgForUrl(urlOrg: string | undefined, ssoBlockedOrgs: SsoBlockedOrg[] | undefined): SsoBlockedOrg | undefined {
  if (!urlOrg) return undefined;
  return ssoBlockedOrgs?.find(org => org.name.toLowerCase() === urlOrg.toLowerCase());
}

/**
 * Intercepts deep links into an org that GitHub is silently hiding from the
 * user's account because their OAuth token lost its SAML SSO authorization.
 *
 * Without this, a user landing on /<blocked-org>/... gets a quietly degraded
 * page: backend data may still load (the user-installation link is kept
 * intact), but the org is missing from the org selector and per-org feature
 * flags read false. Instead, show the same full-screen re-authorization
 * walkthrough used when every org is hidden. Its "Reconnect GitHub" action
 * runs the silent OAuth re-login, during which GitHub's own consent screen
 * lists each org awaiting SSO authorization.
 */
export function SsoBlockedOrgGate(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const isOrgRoute = router.pathname.includes("[org]") && !router.pathname.startsWith("/admin");
  const urlOrg = isOrgRoute ? router.query.org as string | undefined : undefined;
  const blockedOrg = findSsoBlockedOrgForUrl(urlOrg, connectedGithubOrgs?.sso_blocked_orgs);
  if (!blockedOrg) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== queryClient) {
    t1 = <GithubSsoReauthorizationError onRetry={() => {
      invalidateConnectedGithubOrgs(queryClient);
    }} />;
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}